import { alertClasses } from '@mui/material/Alert';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { filledInputClasses } from '@mui/material/FilledInput';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { Components, createTheme as createMuiTheme } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { tableRowClasses } from '@mui/material/TableRow';

import { breakpoints } from '../breakpoints';
import { elevation } from '../elevation';
import { sizing } from '../sizing';

import '@mui/lab/themeAugmentation';
import {
  CheckboxIconChecked,
  CheckboxIconIndeterminate,
  CheckboxIconUnchecked,
} from './checkboxIcons';
import { baseTypography } from './typography';

const muiTheme = createMuiTheme();

export const baseComponents: Components & { MuiDataGrid: any } = {
  MuiAlert: {
    styleOverrides: {
      action: {
        paddingTop: 0,
      },
      message: {
        a: {
          textDecoration: 'underline',
        },
      },
      root: {
        ...baseTypography.body2,
        '.MuiAlert-icon': {
          alignItems: 'center',
        },
        [`.${alertClasses.action}`]: {
          alignItems: 'center',
        },
      },
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: {
        letterSpacing: 0.15,
        marginBottom: 0,
        marginTop: 0,
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      inputRoot: {
        [`&.${inputBaseClasses.sizeSmall}`]: {
          ...baseTypography.body2,
          [`& .${inputBaseClasses.input}`]: {
            ...baseTypography.body2,
          },
        },
      },
      paper: {
        boxShadow: elevation.dropdown,
      },
      tag: {
        ...baseTypography.body2,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: 0,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      outlinedSizeLarge: {
        padding: '7px 21px',
      },
      outlinedSizeMedium: {
        padding: '5px 15px',
      },
      outlinedSizeSmall: {
        padding: '3px 9px',
      },
      root: {
        borderRadius: '8px',
        textTransform: 'none',
      },
      sizeLarge: {
        padding: '8px 22px',
      },
      sizeMedium: {
        padding: '6px 16px',
      },
      sizeSmall: {
        padding: '4px 10px',
      },
      textSizeMedium: {
        fontWeight: 600,
      },
      textSizeSmall: {
        lineHeight: '170%',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 20,
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        '&:last-child': {
          paddingBottom: '24px',
        },
        padding: '24px 24px',
      },
    },
  },
  MuiCardHeader: {
    defaultProps: {
      subheaderTypographyProps: {
        marginTop: 0.5,
        variant: 'body2',
        whiteSpace: 'pre-line',
      },
      titleTypographyProps: {
        variant: 'h6',
      },
    },
    styleOverrides: {
      root: {
        padding: '32px 24px 16px',
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      checkedIcon: <CheckboxIconChecked />,
      color: 'primary',
      icon: <CheckboxIconUnchecked />,
      indeterminateIcon: <CheckboxIconIndeterminate />,
    },
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
      sizeSmall: {
        height: 32,
        padding: '6px',
        width: 32,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        '& span': {
          fontFeatureSettings: "'clig' off",
          letterSpacing: '0.16px',
          lineHeight: '18px',
          paddingLeft: '10px',
          paddingRight: '10px',
        },
        borderRadius: '12px',
        fontWeight: 500,
        height: '24px',
        paddingBottom: '3px',
        paddingTop: '3px',
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        [`@media (min-width: ${breakpoints.sm}px)`]: {
          paddingLeft: 40,
          paddingRight: 40,
        },
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      '#nprogress': {
        pointerEvents: 'none',
      },
      '#nprogress .bar': {
        height: 3,
        left: 0,
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 2000,
      },
      '#root, #__next': {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      },
      '*': {
        boxSizing: 'border-box',
      },
      body: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
      },
      html: {
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      columnHeader: {
        borderRadius: '8px 8px 0 0 ',
        padding: '0px 8px',
      },
      filterForm: {
        alignItems: 'flex-end',
      },
      root: {
        '& .MuiDataGrid-cell': {
          height: sizing.tableRowHeight,
          paddingBottom: 0,
          paddingTop: 0,
        },
        borderRadius: 1.5,
      },
    },
  },
  MuiDialog: {
    defaultProps: {
      onClick: (event) => event.stopPropagation(),
    },
    styleOverrides: {
      paper: {
        borderRadius: '20px',
        maxWidth: 600,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        '&&': {
          padding: '16px 24px',
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '20px 24px 4px',
      },
    },
  },
  MuiDrawer: {
    defaultProps: {
      onClick: (event) => event.stopPropagation(),
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      input: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '24px',
      },
      root: {
        '&:after': {
          display: 'none',
        },
        '&:before': {
          display: 'none',
        },
        borderRadius: 8,
        borderStyle: 'solid',
        borderWidth: 1,
        overflow: 'hidden',
        transition: muiTheme.transitions.create(['border-color', 'box-shadow']),
      },
    },
  },
  MuiFormHelperText: {
    defaultProps: {
      component: 'div',
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: 14,
        fontWeight: 500,
        [`&.${inputLabelClasses.filled}`]: {
          transform: 'translate(12px, 18px) scale(1)',
        },
        [`&.${inputLabelClasses.shrink}`]: {
          [`&.${inputLabelClasses.standard}`]: {
            transform: 'translate(0, -1.5px) scale(0.85)',
          },
          [`&.${inputLabelClasses.filled}`]: {
            transform: 'translate(12px, 6px) scale(0.85)',
          },
          [`&.${inputLabelClasses.outlined}`]: {
            transform: 'translate(14px, -9px) scale(0.85)',
          },
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
      sizeSmall: {
        borderRadius: '6px',
        padding: 4,
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      input: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '24px',
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        '&::placeholder': {
          opacity: 1,
        },
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        overflow: 'hidden',
      },
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'hover',
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        marginRight: '16px',
        minWidth: 'unset',
      },
    },
  },
  MuiMenu: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      list: {
        maxHeight: '40vh',
        minWidth: 150,
        padding: 8,
      },
      paper: {
        boxShadow: elevation.dropdown,
      },
    },
  },
  MuiMenuItem: {
    defaultProps: {
      component: 'button',
    },
    styleOverrides: {
      root: {
        borderRadius: '8px',
        padding: '6px 8px',
        width: '100%',
        ...baseTypography.body1,
        [`&.${autocompleteClasses.option}`]: {
          ...baseTypography.body2,
        },
        '&.bold': {
          fontWeight: 600,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '24px',
      },
      notchedOutline: {
        transition: muiTheme.transitions.create(['border-color', 'box-shadow']),
      },
      root: {
        [`&.${outlinedInputClasses.focused}`]: {
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderWidth: '3px',
          },
        },
        [`&.${filledInputClasses.error}`]: {
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderWidth: '3px',
          },
        },
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
      },
    },
  },
  MuiPopover: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      paper: {
        boxShadow: elevation.dropdown,
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        [`.${inputBaseClasses.root}.${inputBaseClasses.sizeSmall} &`]: {
          ...baseTypography.body2,
        },
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      track: {
        opacity: 1,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        '& + &': {
          marginLeft: 24,
        },
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.71,
        minHeight: 0,
        minWidth: 'auto',
        paddingLeft: 0,
        paddingRight: 0,
        textTransform: 'none',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        [`&:first-of-type`]: {
          paddingLeft: 24,
        },
        [`&:last-child`]: {
          paddingRight: 24,
        },
        borderBottom: 'none',
        padding: '0 16px',
        whiteSpace: 'nowrap',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid',
        borderTopLeftRadius: 8,
        height: sizing.tableRowHeight,
        [`& .${tableCellClasses.root}`]: {
          borderBottom: 'none',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 0.5,
          lineHeight: 1,
          paddingBottom: 0,
          paddingTop: 0,
          textTransform: 'uppercase',
        },
        [`& .${tableCellClasses.root}:first-of-type`]: {
          borderTopLeftRadius: '8px',
          paddingLeft: 24,
        },
        [`& .${tableCellClasses.root}:last-child`]: {
          borderTopRightRadius: '8px',
          paddingRight: 24,
        },
        [`& .${tableCellClasses.paddingCheckbox}`]: {
          paddingBottom: 4,
          paddingTop: 4,
        },
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: {
        bottom: 0,
        flexShrink: 0,
        position: 'sticky',
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:not(:last-child)': {
          borderBottom: '1px solid',
        },
        [`&:last-of-type:not(.${tableRowClasses.head}) .${tableCellClasses.root}:first-of-type`]: {
          borderBottomLeftRadius: '8px',
        },
        [`&:last-of-type:not(.${tableRowClasses.head}) .${tableCellClasses.root}:last-of-type`]: {
          borderBottomRightRadius: '8px',
        },
        [`&.${tableRowClasses.hover}`]: {
          cursor: 'pointer',
        },
        height: sizing.tableRowHeight,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 0,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'filled',
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        justifyContent: 'flex-start',
        textAlign: 'left',
        textTransform: 'none',
      },
    },
  },
  MuiTooltip: {
    defaultProps: {
      enterTouchDelay: 0,
      leaveTouchDelay: 120_000, // tooltip hides automatically if short tapped on mobile, so we increase this delay to prevent it
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        body1Bold: 'p',
        body2Bold: 'p',
      },
    },
  },
};
